/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'react-dom',
	'./TabsMenuBar'
], function(React, ReactDOM, TabsMenuBar) {

	class ResponsiveMenuController {
		/**
		 * @param {object} options - the options object for the extension.
		 * @param {string} options.content - the content of the extension.
		 * @param {Canvas} options.features['Canvas'] - the Canvas object.
		 * @param {Icons} options.features['Icons'] - the Icons feature object.
		 */
		constructor(options) {
			this._content = options.content;
			this._features = options.features;
		}

		/**
		 * Destroys the menu bar.
		 * @returns None
		 */
		destroy() {
			if (this._menuBarContainer) {
				this._features['Canvas'].off('change:selections:select', this._onSelectionChange, this);
				ReactDOM.unmountComponentAtNode(this._menuBarContainer[0]);
				this._menuBarContainer = null;
				this._tabsInfo = null;
			}
		}

		/**
		 * Returns the dom element of the container view.
		 * @returns {object} - the dom element of the container view.
		 */
		getContainer() {
			return this._container;
		}

		/**
		 * set the dom element of the container view.
		 * @returns {object} - the dom element of the container view.
		 */
		setContainer(container) {
			this._container = container;
		}

		/**
		 * Initializes the menu bar.
		 * @returns None
		 */
		_init() {
			const queryMarkerElement = document.createElement('div');
			queryMarkerElement.className = 'mediaQueryMarker';
			const tabMenuContainer = document.createElement('div');
			tabMenuContainer.className = 'responsive-tab-menu-bar-container';
			this.getContainer().prepend(tabMenuContainer);
			this.getContainer().prepend(queryMarkerElement);
			this.updateMenuBar();
			this._registerSelectionChangeListener();
		}

		_onSelectionChange (event)  {
			const id = event.info.value[0];
			const content = this._features['Canvas'].getContent(id);
			// we are only monitoring the the page under the top layout tabs ..
			if  (content && content.getType() === 'page' && content.getContainer() === this._content){
				this.updateMenuBar(id);
			}
		}

		_registerSelectionChangeListener(){
			// monitor selected tab changes
			this._features['Canvas'].on('change:selections:select', this._onSelectionChange, this);
		}

		_getTabsInfo(){
			if (!this._tabsInfo){
				const selectedPage = this._getSelectedPage();
				const tabs = [];
				let selectedTab = null;
				this._content.getChildren().forEach((content) => {
					const tab = {};
					const layoutModel = ResponsiveMenuController._getLayoutModel(content);
					// Delayed pages will not register the property provider.. until we cleanup the properties
					// we need to check the layout model for the title..
					tab.label = content.getPropertyValue('title') || layoutModel.get('title');
					const tabIcon = layoutModel.get('tabIcon');
					if (tabIcon && tabIcon !== 'transparent') {
						tab.icon = this._features['Icons'].getIcon(tabIcon).id;
					}
					tab.id = content.getId();
					tabs.push(tab);
					if ((selectedPage === content || selectedPage.getId() === content.getId())) {
						selectedTab = content.getId();
					}
				});
				this._tabsInfo = {
					tabs: tabs,
					selectedTab: selectedTab
				};
			}
			return this._tabsInfo;
		}

		/**
		* Updates the menu bar with the current tabs.
		* @returns None
		*/
		updateMenuBar(tabId = null) {
			const tabs = this._getTabsInfo();
			tabs.selectedTab = tabId ? tabId: tabs.selectedTab;
			ReactDOM.render(
				<TabsMenuBar
					tabs={tabs.tabs}
					selectedTab={tabs.selectedTab}
					onTabChange={this._onTabChange.bind(this)}
					backgroundColor={this._getMenuColor()}
				/>,
				this._getMenuBarContainer()[0]
			);
		}

		/**
		 * Gets the background color of the container.
		 * @returns {string} The background color of the container.
		 */
		_getMenuColor() {
			const color = window.getComputedStyle(this.getContainer()).backgroundColor;
			return color === 'rgba(0, 0, 0, 0)' ? 'white' : color;
		}

		/**
		 * Gets the menu bar container element.
		 * @returns {object} The menu bar container element.
		 */
		_getMenuBarContainer() {
			if (!this._menuBarContainer) {
				this._menuBarContainer = this.getContainer().getElementsByClassName('responsive-tab-menu-bar-container');
			}
			return this._menuBarContainer;
		}

		/**
		 * Gets the selected page from the canvas.
		 * @returns {Page} the selected page.
		 */
		_getSelectedPage() {
			return this._features['Canvas'].getSelectedContentList({ type: 'page' })[0];
		}

		/**
		 * Gets the layout model for the content.
		 * @param {Object} content - the content object to obtain the layout model for.
		 * @returns {LayoutModel} the layout model of the content object.
		 */
		static _getLayoutModel(content) {
			return content.getFeature('Models.internal').getLayoutModel();
		}

		/**
		 * Handles the tab change event.
		 * @param {string} tabId - the id of the tab that was changed.
		 * @returns None
		 */
		_onTabChange (tabId) {
			this._features['Canvas'].selectContent([tabId]);
		}

		/**
		 * Renders the responsive menu UI.
		 * @returns None
		 */
		renderResponsiveMenuUI() {
			if (!this.isResponsiveMenuRendered && this._content.getPropertyValue('showTabsInPreview') ) {
				this.isResponsiveMenuRendered = true;
				this._init();
			}
		}
	}

	return ResponsiveMenuController;
});
